import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { usePostHog } from 'posthog-js/react';

function StravaAuthCallbackPage() {
    const [message, setMessage] = useState('Connecting to Strava...');
    const [isLoading, setIsLoading] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const posthog = usePostHog();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const stravaError = params.get('error');
        const returnedState = params.get('state');

        // Retrieve state ONCE and then remove it to prevent reuse.
        const storedState = localStorage.getItem('stravaOAuthState');
        localStorage.removeItem('stravaOAuthState'); // Clean up stored state immediately after retrieval

        // Clean the URL bar (can be done early)
        if (window.history.replaceState) {
            const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({ path: cleanUrl }, document.title, cleanUrl);
        }

        if (stravaError) {
            setMessage("We couldn't connect to your Strava account. Please try again.");
            setIsLoading(false);
            console.error("Strava OAuth Error:", stravaError);
            setTimeout(() => navigate('/'), 5000);
            return;
        }

        if (!code) {
            setMessage("Connection was cancelled. Please try connecting to Strava again.");
            setIsLoading(false);
            setTimeout(() => navigate('/'), 5000);
            return;
        }

        // Crucial: Check state *after* retrieving and removing it.
        if (!storedState || returnedState !== storedState) {
            setMessage("Security check failed. Please try connecting to Strava again.");
            setIsLoading(false);
            console.error("Strava OAuth State Mismatch Error. Stored: ", storedState, "Returned: ", returnedState);
            setTimeout(() => navigate('/'), 7000);
            return;
        }

        setMessage("Verifying with Mythic Maps...");

        const processStravaAuth = async () => {
            try {
                const signInOrLinkWithStrava = httpsCallable(functions, 'signInOrLinkWithStrava');
                const result = await signInOrLinkWithStrava({ code: code, requestedScope: "activity:read,activity:read_all,activity:write" });

                if (result.data && result.data.success) {
                    if (result.data.customToken) {
                        await signInWithCustomToken(auth, result.data.customToken);
                        
                        // Identify user in PostHog with their details
                        if (auth.currentUser && posthog) {
                            posthog.identify(auth.currentUser.uid, {
                                name: auth.currentUser.displayName,
                                email: auth.currentUser.email,
                                signup_method: 'strava',
                                strava_connected: true
                            });
                        }
                        
                        if (result.data.isRestored) {
                            setMessage("Welcome back! Your account has been restored.");
                        } else {
                            setMessage("Successfully connected! Welcome to Mythic Maps!");
                        }
                    } else {
                        setMessage("Strava account successfully linked!");
                        
                        // For existing users linking Strava, still identify them
                        if (auth.currentUser && posthog) {
                            posthog.identify(auth.currentUser.uid, {
                                name: auth.currentUser.displayName,
                                email: auth.currentUser.email,
                                strava_connected: true
                            });
                        }
                    }
                    setIsSuccess(true);
                    setIsLoading(false);
                    
                    if (window.opener && !window.opener.closed) {
                        window.opener.postMessage("stravaAuthSuccess", window.location.origin);
                    }
                    setTimeout(() => navigate('/'), 2000);

                    posthog?.capture('strava_auth_callback_success', {
                        timestamp: new Date().toISOString()
                    });
                    
                    if (result.data.isNewUser) {
                        posthog?.capture('new_user_signup', {
                            signup_method: 'strava',
                            timestamp: new Date().toISOString()
                        });
                    } else if (result.data.isRestored) {
                        posthog?.capture('account_restored', {
                            restoration_method: 'strava',
                            timestamp: new Date().toISOString()
                        });
                    }
                } else {
                    const errorMessage = (result.data && result.data.error) || "Something went wrong. Please try again.";
                    setMessage(errorMessage);
                    setIsLoading(false);
                    console.error("signInOrLinkWithStrava Error:", errorMessage);
                    setTimeout(() => navigate('/'), 5000);

                    posthog?.capture('strava_auth_callback_failed', {
                        error_message: errorMessage
                    });
                }
            } catch (err) {
                setMessage("Connection failed. Please check your internet and try again.");
                setIsLoading(false);
                console.error("Cloud Function Call Error (signInOrLinkWithStrava):", err);
                setTimeout(() => navigate('/'), 5000);

                posthog?.capture('strava_auth_callback_failed', {
                    error_message: err.message
                });
            }
        };

        processStravaAuth();

    }, [location, navigate, posthog]); // location should be sufficient to re-trigger if URL changes

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--color-bg-page, #FFF)',
            fontFamily: 'var(--font-primary, Inter, sans-serif)',
            padding: '20px'
        }}>
            <div style={{
                background: 'var(--color-bg-content-area, #FFF)',
                borderRadius: 'var(--border-radius-lg, 12px)',
                padding: '48px 32px',
                boxShadow: 'var(--box-shadow-card)',
                border: '1px solid var(--color-border-card, #EAECF0)',
                textAlign: 'center',
                maxWidth: '400px',
                width: '100%'
            }}>
                <h2 style={{
                    color: 'var(--color-text-primary, #101828)',
                    fontSize: '24px',
                    fontWeight: '600',
                    margin: '0 0 24px 0'
                }}>
                    {isSuccess ? 'Connection Successful!' : 'Connecting to Strava'}
                </h2>
                
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                    marginBottom: '24px'
                }}>
                    {isLoading ? (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid var(--Gray-200, #EAECF0)',
                            borderTop: '3px solid var(--Brand-Strava-Orange, #FC5200)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                    ) : isSuccess ? (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'var(--Brand-Strava-Orange, #FC5200)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '20px'
                        }}>
                            ✓
                        </div>
                    ) : (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'var(--Gray-300, #D0D5DD)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '20px'
                        }}>
                            !
                        </div>
                    )}
                </div>

                <p style={{
                    color: 'var(--color-text-secondary, #344054)',
                    fontSize: '16px',
                    lineHeight: '1.5',
                    margin: '0 0 20px 0'
                }}>
                    {message}
                </p>

                {!isLoading && (
                    <p style={{
                        color: 'var(--color-text-subtle, #667085)',
                        fontSize: '14px',
                        margin: '0'
                    }}>
                        Redirecting you back to Mythic Maps...
                    </p>
                )}
            </div>
            
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default StravaAuthCallbackPage; 